import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { projects, ApiError } from '../api.js';

export function NewProjectPage() {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [script, setScript] = useState('');
  const [intent, setIntent] = useState('');
  const [tone, setTone] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handle(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const r = await projects.create({
        title: title.trim() || guessTitle(script),
        script: script.trim(),
        intent: intent.trim() || undefined,
        tone_preference: tone.trim() || undefined,
      });
      navigate(`/projects/${r.project.id}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create project.');
      setSubmitting(false);
    }
  }

  return (
    <div className="max-w-3xl mx-auto">
      <h1 className="font-serif text-3xl mb-2">New project</h1>
      <p className="text-muted text-sm mb-8">
        Paste the narration script. The director reads it end-to-end and writes a directing plan.
      </p>

      <form onSubmit={handle} className="space-y-6">
        <Field label="Title" hint="optional — we’ll guess from the first line if you leave it blank">
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. The Watcher Letters — Cold Open"
            className="w-full px-3 py-2 bg-white border border-soft rounded-md focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/60 text-sm"
          />
        </Field>

        <Field label="Script" required>
          <textarea
            value={script}
            onChange={(e) => setScript(e.target.value)}
            required
            minLength={20}
            rows={14}
            placeholder="Paste your narration here…"
            className="w-full px-4 py-3 bg-white border border-soft rounded-md focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/60 font-serif text-base leading-relaxed resize-y"
          />
          <span className="text-xs text-muted/80 mt-1 block">
            {script.split(/\s+/).filter(Boolean).length} words · {script.length} characters
          </span>
        </Field>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Field label="Intent (optional)" hint="What is this script for?">
            <input
              value={intent}
              onChange={(e) => setIntent(e.target.value)}
              placeholder="e.g. YouTube true crime cold open"
              className="w-full px-3 py-2 bg-white border border-soft rounded-md focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/60 text-sm"
            />
          </Field>
          <Field label="Tone preference (optional)" hint="Any steering for delivery">
            <input
              value={tone}
              onChange={(e) => setTone(e.target.value)}
              placeholder="e.g. colder, drier, more restrained"
              className="w-full px-3 py-2 bg-white border border-soft rounded-md focus:outline-none focus:ring-2 focus:ring-accent/40 focus:border-accent/60 text-sm"
            />
          </Field>
        </div>

        {error && (
          <div className="text-sm text-accent bg-accent/5 border border-accent/20 rounded-md px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={submitting || script.trim().length < 20}
            className="bg-ink text-parchment px-5 py-2 rounded-md hover:bg-ink/90 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm tracking-wide"
          >
            {submitting ? 'Creating…' : 'Create project'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/projects')}
            className="text-muted hover:text-ink text-sm transition-colors px-3 py-2"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({
  label,
  hint,
  required,
  children,
}: {
  label: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="block text-sm text-ink/80 mb-1.5">
        {label}
        {required && <span className="text-accent">*</span>}
        {hint && <span className="text-muted/70 font-normal ml-2">— {hint}</span>}
      </span>
      {children}
    </label>
  );
}

function guessTitle(script: string): string {
  const firstLine = script.trim().split('\n')[0]?.trim() ?? '';
  if (firstLine.length > 0 && firstLine.length <= 80) return firstLine;
  if (firstLine.length > 80) return firstLine.slice(0, 77) + '…';
  return 'Untitled';
}
